import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { useToast } from '@/shared/components/Toast';
import { useCreateManualReferral } from '../../hooks/useCreateManualReferral';
import {
  useManualReferralForEdit,
  useUpdateManualReferral,
} from '../../hooks/useManualReferralEdit';
import { importBatchTip } from '../engagement-metrics/referrals/referralsCopy';
import { ManualReferralCareSeekerSelect } from './ManualReferralCareSeekerSelect';
import { ManualReferralEntitySelect } from './ManualReferralEntitySelect';
import { ManualReferralSummary } from './ManualReferralSummary';
import { NewCareSeekerFields } from './NewCareSeekerFields';
import {
  NewProfessionalContactFields,
  emptyNewProfessionalContact,
} from './NewProfessionalContactFields';
import {
  buildPayload,
  emptyNewCareSeeker,
  formStateFromPrefill,
  getTodayDateInputValue,
  modeButtonClass,
  roleLabel,
  validateForm,
  type CareSeekerMode,
  type FormErrors,
  type LockedReferredEntity,
  type ManualReferralFormState,
} from './manualReferralForm';

// Create mode: launched from a profile, referred party locked to it; without
// `referredEntity` (Referrals tab, feature 117) the referred party is picked
// from search. Edit mode: `referralId` loads the prefill; referred unlocked.
type ManualReferralModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & (
  | { referredEntity?: LockedReferredEntity; referralId?: never }
  | { referralId: string; referredEntity?: never }
);

// Date input shows the day; an untouched date re-sends the original timestamp.
const toDateInputValue = (iso: string) => iso.slice(0, 10);

const initialFormState = (
  referredMode: ManualReferralFormState['referredMode'] = 'locked',
): ManualReferralFormState => ({
  referringMode: 'search',
  referringProfessional: null,
  newReferringContact: emptyNewProfessionalContact,
  referredMode,
  referred: null,
  newReferredContact: emptyNewProfessionalContact,
  onBehalfOfMode: 'none',
  onBehalfOf: null,
  newOnBehalfOfContact: emptyNewProfessionalContact,
  careSeekerMode: 'search',
  careSeeker: null,
  newCareSeeker: emptyNewCareSeeker,
});

export const ManualReferralModal = ({
  open,
  onOpenChange,
  referredEntity: lockedEntity,
  referralId,
}: ManualReferralModalProps) => {
  const referredEntity = lockedEntity ?? null;
  const isEdit = Boolean(referralId);
  const { showToast } = useToast();
  const createManualReferral = useCreateManualReferral();
  const updateManualReferral = useUpdateManualReferral();
  const prefillQuery = useManualReferralForEdit(referralId, open && isEdit);
  const prefill = isEdit ? prefillQuery.data : undefined;
  const isLoadingPrefill = isEdit && !prefill;
  const [form, setForm] = useState<ManualReferralFormState>(() =>
    initialFormState(referredEntity ? 'locked' : 'search'),
  );
  const [referralOccurredAt, setReferralOccurredAt] = useState(getTodayDateInputValue);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!open) return;
    setForm(
      prefill
        ? formStateFromPrefill(prefill, initialFormState())
        : initialFormState(referredEntity ? 'locked' : 'search'),
    );
    setReferralOccurredAt(
      prefill ? toDateInputValue(prefill.referralOccurredAt) : getTodayDateInputValue(),
    );
    setNotes(prefill?.notes ?? '');
    setErrors({});
  }, [open, referredEntity?.id, prefill]);

  const updateForm = (patch: Partial<ManualReferralFormState>, clear?: (keyof FormErrors)[]) => {
    setForm((current) => ({ ...current, ...patch }));
    if (clear?.length) {
      setErrors((current) => {
        const next = { ...current };
        clear.forEach((key) => {
          delete next[key];
        });
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form, referredEntity);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = buildPayload({ state: form, referredEntity, referralOccurredAt, notes });
    try {
      if (prefill) {
        if (referralOccurredAt === toDateInputValue(prefill.referralOccurredAt)) {
          payload.referralOccurredAt = prefill.referralOccurredAt;
        }
        await updateManualReferral.mutateAsync({ id: prefill.id, payload });
        showToast('Saved', 'success');
      } else {
        await createManualReferral.mutateAsync(payload);
        showToast('Manual referral registered successfully.', 'success');
      }
      onOpenChange(false);
    } catch (error) {
      const fallback = isEdit
        ? 'Unable to save the referral.'
        : 'Unable to register manual referral.';
      const message = error instanceof Error ? error.message : fallback;
      setErrors({ submit: message });
      showToast(message, 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="sticky top-0 z-10 border-b bg-white px-6 py-5 pr-12">
          <DialogTitle>{isEdit ? 'Edit referral' : 'Register manual referral'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes update metrics and billing. Pending follow-up emails are cancelled if the referred professional or care seeker changes. Nothing is re-sent.'
              : referredEntity
                ? 'Register a referral made outside the platform from a professional profile.'
                : 'Register a referral made outside the platform.'}
          </DialogDescription>
          {prefill?.importBatch && (
            <p
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="note"
            >
              {importBatchTip(prefill.importBatch)}
            </p>
          )}
        </DialogHeader>

        {isLoadingPrefill ? (
          <div className="flex-1 px-6 py-5 text-sm text-gray-600" role="status">
            {prefillQuery.error ? 'Unable to load the referral.' : 'Loading referral...'}
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Referred party: locked to the launching profile (create) or any
              professional from search (edit), with a "not in the app" escape
              (feature 101). */}
            {form.referredMode === 'search' ? (
              <div className="space-y-2">
                <ManualReferralEntitySelect
                  label="Referred professional"
                  searchLabel="Referred professional search"
                  selected={form.referred}
                  allowedRoles={['FINANCIAL_ADVISOR', 'SERVICE_PROVIDER']}
                  onSelect={(entity) => updateForm({ referred: entity }, ['referred'])}
                />
                {errors.referred && <p className="text-sm text-red-600">{errors.referred}</p>}
                <button
                  type="button"
                  className="text-sm text-[#026857] underline-offset-2 hover:underline"
                  onClick={() => updateForm({ referredMode: 'new' }, ['referred'])}
                >
                  Referred someone not in the app instead
                </button>
              </div>
            ) : form.referredMode === 'locked' && referredEntity ? (
              <div className="space-y-2">
                <ManualReferralSummary
                  title={`Referred ${roleLabel(referredEntity.role)}`}
                  entity={referredEntity}
                />
                <button
                  type="button"
                  className="text-sm text-[#026857] underline-offset-2 hover:underline"
                  onClick={() => updateForm({ referredMode: 'new' })}
                >
                  Referred someone not in the app instead
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Referred professional (not in the app)</Label>
                <NewProfessionalContactFields
                  idPrefix="manual-referral-referred-contact"
                  value={form.newReferredContact}
                  errors={errors.newReferredContact ?? {}}
                  onChange={(value) =>
                    updateForm({ newReferredContact: value }, ['newReferredContact'])
                  }
                />
                <button
                  type="button"
                  className="text-sm text-[#026857] underline-offset-2 hover:underline"
                  onClick={() =>
                    updateForm(
                      {
                        referredMode: referredEntity ? 'locked' : 'search',
                        newReferredContact: emptyNewProfessionalContact,
                      },
                      ['newReferredContact'],
                    )
                  }
                >
                  {referredEntity ? `Back to ${referredEntity.label}` : 'Search existing instead'}
                </button>
              </div>
            )}

            {/* Referring party: search | new contact | bQuest Concierge */}
            <div className="space-y-2">
              <Label>Referring party</Label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Referring party type">
                <button
                  type="button"
                  aria-pressed={form.referringMode === 'search'}
                  className={modeButtonClass(form.referringMode === 'search')}
                  onClick={() =>
                    updateForm({ referringMode: 'search' }, [
                      'referringProfessional',
                      'newReferringContact',
                    ])
                  }
                >
                  Search existing
                </button>
                <button
                  type="button"
                  aria-pressed={form.referringMode === 'new'}
                  className={modeButtonClass(form.referringMode === 'new')}
                  onClick={() =>
                    updateForm({ referringMode: 'new', referringProfessional: null }, [
                      'referringProfessional',
                      'newReferringContact',
                    ])
                  }
                >
                  Add someone not in the app
                </button>
                <button
                  type="button"
                  aria-pressed={form.referringMode === 'concierge'}
                  className={modeButtonClass(form.referringMode === 'concierge')}
                  onClick={() =>
                    updateForm({ referringMode: 'concierge', referringProfessional: null }, [
                      'referringProfessional',
                      'newReferringContact',
                    ])
                  }
                >
                  bQuest Concierge
                </button>
              </div>

              {form.referringMode === 'search' && (
                <>
                  <ManualReferralEntitySelect
                    label=""
                    searchLabel="Referring professional search"
                    selected={form.referringProfessional}
                    allowedRoles={['FINANCIAL_ADVISOR', 'SERVICE_PROVIDER']}
                    onSelect={(entity) =>
                      updateForm({ referringProfessional: entity }, ['referringProfessional'])
                    }
                  />
                  {errors.referringProfessional && (
                    <p className="text-sm text-red-600">{errors.referringProfessional}</p>
                  )}
                </>
              )}
              {form.referringMode === 'new' && (
                <NewProfessionalContactFields
                  idPrefix="manual-referral-referring-contact"
                  value={form.newReferringContact}
                  errors={errors.newReferringContact ?? {}}
                  onChange={(value) =>
                    updateForm({ newReferringContact: value }, ['newReferringContact'])
                  }
                />
              )}
              {form.referringMode === 'concierge' && (
                <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  The bQuest Concierge team made this referral. The recording admin is tracked
                  automatically.
                </p>
              )}
            </div>

            {/* Referred on behalf of (optional) */}
            <div className="space-y-2">
              <Label>Referred on behalf of (optional)</Label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="On behalf of type">
                <button
                  type="button"
                  aria-pressed={form.onBehalfOfMode === 'none'}
                  className={modeButtonClass(form.onBehalfOfMode === 'none')}
                  onClick={() =>
                    updateForm({ onBehalfOfMode: 'none', onBehalfOf: null }, [
                      'onBehalfOf',
                      'newOnBehalfOfContact',
                    ])
                  }
                >
                  None
                </button>
                <button
                  type="button"
                  aria-pressed={form.onBehalfOfMode === 'search'}
                  className={modeButtonClass(form.onBehalfOfMode === 'search')}
                  onClick={() =>
                    updateForm({ onBehalfOfMode: 'search' }, ['onBehalfOf', 'newOnBehalfOfContact'])
                  }
                >
                  Search existing
                </button>
                <button
                  type="button"
                  aria-pressed={form.onBehalfOfMode === 'new'}
                  className={modeButtonClass(form.onBehalfOfMode === 'new')}
                  onClick={() =>
                    updateForm({ onBehalfOfMode: 'new', onBehalfOf: null }, [
                      'onBehalfOf',
                      'newOnBehalfOfContact',
                    ])
                  }
                >
                  Add someone not in the app
                </button>
              </div>

              {form.onBehalfOfMode === 'search' && (
                <>
                  <ManualReferralEntitySelect
                    label=""
                    searchLabel="On behalf of search"
                    selected={form.onBehalfOf}
                    allowedRoles={['FINANCIAL_ADVISOR', 'SERVICE_PROVIDER']}
                    onSelect={(entity) => updateForm({ onBehalfOf: entity }, ['onBehalfOf'])}
                  />
                  {errors.onBehalfOf && <p className="text-sm text-red-600">{errors.onBehalfOf}</p>}
                </>
              )}
              {form.onBehalfOfMode === 'new' && (
                <NewProfessionalContactFields
                  idPrefix="manual-referral-on-behalf-of-contact"
                  value={form.newOnBehalfOfContact}
                  errors={errors.newOnBehalfOfContact ?? {}}
                  onChange={(value) =>
                    updateForm({ newOnBehalfOfContact: value }, ['newOnBehalfOfContact'])
                  }
                />
              )}
            </div>

            <ManualReferralCareSeekerSelect
              mode={form.careSeekerMode}
              selectedCareSeeker={form.careSeeker}
              onModeChange={(mode: CareSeekerMode) =>
                updateForm(
                  mode === 'search'
                    ? { careSeekerMode: mode }
                    : { careSeekerMode: mode, careSeeker: null },
                  ['careSeeker', 'newCareSeeker'],
                )
              }
              onSelectExisting={(entity) => updateForm({ careSeeker: entity }, ['careSeeker'])}
            />
            {errors.careSeeker && <p className="text-sm text-red-600">{errors.careSeeker}</p>}

            {form.careSeekerMode === 'new' && (
              <NewCareSeekerFields
                value={form.newCareSeeker}
                errors={errors.newCareSeeker ?? {}}
                onChange={(value) => updateForm({ newCareSeeker: value }, ['newCareSeeker'])}
              />
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="manual-referral-occurred-at">Referral date</Label>
                <Input
                  id="manual-referral-occurred-at"
                  type="date"
                  value={referralOccurredAt}
                  onChange={(event) => setReferralOccurredAt(event.target.value)}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="manual-referral-notes">Notes</Label>
                <Textarea
                  id="manual-referral-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Optional context for admin records"
                />
              </div>
            </div>

            {errors.submit && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {errors.submit}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="sticky bottom-0 z-10 border-t bg-white px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {isEdit ? (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isLoadingPrefill || updateManualReferral.isPending}
            >
              {updateManualReferral.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={createManualReferral.isPending}>
              {createManualReferral.isPending ? 'Registering...' : 'Register referral'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
